<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import SideBar from '@src/partials/side-bar'
import MusicPlayer from '@src/partials/music-player'
import MenuBar from '@src/partials/menu-bar'
import TopBar from '@components/top-bar'
import ConsentBanner from '@components/consent-banner'
import { useStore } from '@composables/use-store'
import consentService from '@services/consent-service'

defineProps({
    hideTopBar: {
        type: Boolean,
        default: false,
    },
    hiddenOverflow: {
        type: Boolean,
        default: false,
    },
    hideMobileMenuBar: {
        type: Boolean,
        default: false,
    },
})

const store = useStore()
const main = ref(null)
const container = ref(null)
const showConsentBanner = ref(false)
const isMenuOpen = computed(() => store.state.appearance.isMainMenuOpen)

function handleWindowScroll() {
    store.commit('appearance/SET_SCROLL', window.pageYOffset)
}

function handleMainScroll() {
    store.commit('appearance/SET_SCROLL', main.value.scrollTop)
}

function hideConsentBanner() {
    showConsentBanner.value = false
}

onMounted(() => {
    store.commit('appearance/SET_SCROLL_CHILD', container.value)
    window.addEventListener('scroll', handleWindowScroll, { passive: true })
    main.value.addEventListener('scroll', handleMainScroll, { passive: true })
    showConsentBanner.value = consentService.shouldShowBanner()
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleWindowScroll)
    main.value?.removeEventListener('scroll', handleMainScroll)
})
</script>

<template>
    <div>
        <MenuBar
            v-if="!hideTopBar"
            class="menu-bar"
            :class="{ 'hide-mobile': hideMobileMenuBar }"
        />
        <div
            class="wrapper"
            :class="{ 'stop-scroll': isMenuOpen }"
        >
            <SideBar />
            <div
                ref="main"
                class="main"
                :class="{ 'hide-top': hideTopBar }"
            >
                <div
                    v-if="!hideTopBar"
                    class="top-bar"
                >
                    <BaseSection padded>
                        <div class="top-bar-inner">
                            <TopBar class="top-bar-content" />
                        </div>
                    </BaseSection>
                </div>
                <div
                    ref="container"
                    class="container"
                    :class="{ 'hidden-overflow': hiddenOverflow }"
                >
                    <slot />
                </div>
            </div>
        </div>
        <MusicPlayer />
        <ConsentBanner
            v-if="showConsentBanner"
            @close="hideConsentBanner"
        />
    </div>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.menu-bar {
    position: sticky;
    top: 0;
    height: $size-menu-bar-height;
    background: $c-black;

    &.hide-mobile {
        display: none;
    }
}

.wrapper {
    display: grid;
    min-height: calc(100vh - $size-menu-bar-height - $size-music-player);

    &.stop-scroll {
        overflow: hidden;
    }
}

.main {
    position: relative;
    min-height: 100%;
    overflow: hidden;

    &.hide-top {
        padding-top: 0;
    }
}

.top-bar {
    position: absolute;
    z-index: $layer-dropdown-z-index;
    display: none;
    width: 100%;
    margin-top: gutter();
}

.top-bar-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
}

.top-bar-content {
    flex: 1;
}

.container {
    min-height: 100%;
}

@include desktop {
    .wrapper {
        display: grid;
        grid-template-columns: $size-large-side-bar calc(100vw - #{$size-large-side-bar});
        height: calc(100vh - $size-music-player);

        &.stop-scroll {
            overflow: unset;
        }
    }

    .main {
        height: calc(100vh - #{$size-music-player});
        padding-top: 0;
        padding-bottom: $size-music-player * 8;
        margin-bottom: $size-music-player;
        overflow: auto;
        overflow-x: hidden;
    }

    .top-bar {
        position: sticky;
        top: 0;
        display: block;
        margin-top: 0;
        background: $c-header-bg;
    }

    .top-bar-inner {
        min-height: 80px;
    }

    .container {
        padding-top: $s-m;

        &.hidden-overflow {
            overflow: hidden;
        }
    }
}
</style>
